import { Button } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { Lightbox } from '@/components/modules/lightbox';
import { getImagePreview } from '@/helpers';
import withMobileView from '@/hoc/withMobileView';
import useMobileView from '@/hooks/useMobileView';
import { __, _s } from '@/locale';
import { Place } from '@/types/state/shared';
import { useState } from 'react';

const baseTranslationKey = 'components.modules.place.Photos';

type PhotosProps = {
  photos: string[];
  place: Place;
};

const getImageRatio = (w) => {
  //  ratio of 16:/10
  return Math.round(parseInt(w) * 0.625);
};

const getSecondImageSizes = (length) => {
  let width, height, size;
  switch (length) {
    case 2:
      width = 496;
      height = getImageRatio(496);
      size = 'big';
      break;
    case 3:
      width = 317;
      height = getImageRatio(317);
      size = 'medium';
      break;
    case 4:
      width = 496;
      height = getImageRatio(496);
      size = 'medium_l';
      break;
    case 5:
    default:
      width = 240;
      height = getImageRatio(240);
      size = 'medium';
      break;
  }
  return { width, height, size };
};

const Photos = ({ photos, place }: PhotosProps) => {
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [lightboxIsOpen, setLightboxIsOpen] = useState<boolean>(false);
  const { isMobileView } = useMobileView();

  if (!photos) {
    // No Photos - no gallery
    return null;
  }

  const openLightbox = (index: number) => {
    setCurrentImage(index);
    setLightboxIsOpen(true);
  };

  const closeLightbox = () => {
    setCurrentImage(0);
    setLightboxIsOpen(false);
  };

  const gotoPrevious = () => {
    setCurrentImage((prev) => Math.max(prev - 1, 0));
  };

  const gotoNext = () => {
    setCurrentImage((prev) => (prev + 1) % photos.length);
  };

  const gotoImage = (index: number) => {
    setCurrentImage(index);
  };

  const getLightbox = () => {
    const lightboxPhotos = photos.map((url) => ({ src: url }));
    return (
      <Lightbox
        images={lightboxPhotos}
        onClose={closeLightbox}
        backdropClosesModal
        onClickPrev={gotoPrevious}
        onClickNext={gotoNext}
        currentImage={currentImage}
        isOpen={lightboxIsOpen}
        showThumbnails
        onClickThumbnail={gotoImage}
        imageCountSeparator={__('of')}
      />
    );
  };

  const nrImages = photos.length;
  const mainImages = place?.about?.visibleMainImages || (nrImages > 5 ? 5 : nrImages);
  const showFeaturedImage = (mainImages !== 2 && mainImages !== 4) || isMobileView;
  const secondImage = getSecondImageSizes(mainImages);

  const smallImage = getImagePreview(photos[0], 'medium', place.about.slug + '-' + 1);
  const bigImage = getImagePreview(photos[0], 'big', place.about.slug + '-' + 1);

  return (
    <>
      <div
        id="photos"
        className={`flex ${isMobileView ? 'gap-xs flex-col' : 'gap-md '} ${
          mainImages === 3 ? 'flex-col' : ''
        } scroll-mt-[144px]`}>
        {showFeaturedImage && (
          <div
            key={0}
            className={`relative flex ${
              isMobileView
                ? 'h-[241px] flex-1'
                : mainImages > 4
                  ? 'max-h-[316px] max-w-[510px] min-w-[500px]'
                  : 'min-w-[200px]'
            } cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-[#999] `}
            onClick={() => openLightbox(0)}>
            <div className="relative aspect-[666/416] w-full overflow-hidden max-[325px]:aspect-[325/303]">
              <picture>
                <source srcSet={`${smallImage}`} media="(max-width: 325px)" />

                <img
                  src={bigImage}
                  className={
                    (mainImages > 4
                      ? 'min-w-[500px] max-w-[510px]'
                      : [1, 3].indexOf(mainImages) !== -1
                        ? 'max-w-[648px]'
                        : 'max-w-[510px]') + ' w-full object-cover'
                  }
                  fetchPriority="high"
                  loading="eager"
                  alt=""
                  onError={(e) => (e.currentTarget.src = '/images/bd-fallback-img200x200.png')}
                />
              </picture>
            </div>

            {(isMobileView || place?.about?.visibleMainImages === 1) && photos.length > 1 && (
              <Button
                className="right-xs bottom-xs absolute"
                variant="secondary"
                iconNoFilter
                rightIcon={<Icon variant="image" color="fg_primary" />}
                size="sm"
                label={_s(`${baseTranslationKey}.cta`)}
              />
            )}
          </div>
        )}
        {!isMobileView && mainImages > 1 && photos.length > 1 && (
          <div className={`flex ${mainImages !== 4 ? 'flex-row' : 'flex-col'} gap-md`}>
            <div
              className={`gap-md flex ${
                mainImages === 4 ? 'flex-wrap' : mainImages === 3 || mainImages === 2 ? 'flex-1 flex-row' : 'flex-col'
              }`}>
              {photos.slice(showFeaturedImage ? 1 : 0, showFeaturedImage ? 3 : 2).map((img, k) => {
                return (
                  <div
                    key={k}
                    className={`relative flex flex-1 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-[#999] ${
                      mainImages > 4 ? 'max-h-[150px]' : ''
                    } ${mainImages === 4 ? 'w-[496px]' : ''} ${mainImages === 3 ? 'max-h-[200px]' : ''}
                    `}
                    onClick={() => {
                      openLightbox(k + (showFeaturedImage ? 1 : 0));
                    }}>
                    <img
                      src={getImagePreview(img, secondImage.size, place.about.slug + '-' + (k + 1))}
                      height={secondImage.height}
                      fetchPriority={!showFeaturedImage && k === 0 ? 'high' : 'low'}
                      loading={!showFeaturedImage && k === 0 ? 'eager' : 'lazy'}
                      width={secondImage.width}
                      className={`${mainImages < 5 ? 'h-full w-full object-cover' : 'object-cover'}`}
                      alt=""
                      onError={(e) => (e.currentTarget.src = '/images/bd-fallback-img200x200.png')}
                    />
                    {((mainImages === 2 && k === 1) || (mainImages === 3 && k === 1)) && (
                      <Button
                        className="right-xs bottom-xs absolute"
                        variant="secondary"
                        iconNoFilter
                        rightIcon={<Icon variant="image" color="fg_primary" />}
                        size="sm"
                        label={_s(`${baseTranslationKey}.cta`)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {!isMobileView && mainImages >= 4 && (
              <div
                className={`${isMobileView ? 'gap-xs' : 'gap-md'} flex ${
                  showFeaturedImage ? 'flex-col' : 'flex-row '
                } `}>
                {photos.slice(showFeaturedImage ? 3 : 2, mainImages).map((img, k) => {
                  return (
                    <div
                      key={k}
                      className={`relative flex ${
                        showFeaturedImage ? 'max-h-[150px]' : 'max-h-[310px]'
                      } flex-1 cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-[#999]`}
                      onClick={() => openLightbox(k + (mainImages === 4 ? 2 : 3))}>
                      <img
                        loading="lazy"
                        fetchPriority="low"
                        src={getImagePreview(img, secondImage.size, place.about.slug + '-' + (k + 1))}
                        height={secondImage.height}
                        width={secondImage.width}
                        className={`${mainImages < 5 ? 'h-full w-full object-cover' : 'object-cover'}`}
                        alt=""
                        onError={(e) => (e.currentTarget.src = '/images/bd-fallback-img200x200.png')}
                      />
                      {k === 1 && !isMobileView && (
                        <Button
                          className="right-xs bottom-xs absolute"
                          variant="secondary"
                          iconNoFilter
                          rightIcon={<Icon variant="image" color="fg_primary" />}
                          size="sm"
                          label={_s(`${baseTranslationKey}.cta`)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {lightboxIsOpen && getLightbox()}
      </div>
    </>
  );
};

export default withMobileView(Photos);
